import { Controller } from "@hotwired/stimulus"

// API Dashboard Controller
// Connects to data-controller="api-dashboard"
// Uses ECharts for visualizations

export default class extends Controller {
  static targets = [
    "totalRequests", "requestsToday", "uniqueUsers", "successRate",
    "graphqlRequests", "restRequests", "avgResponseTime", "totalItemsServed",
    "requestsChart", "endpointChart", "queryTypeChart", "hourlyChart",
    "performanceTable", "topUsersTable", "recentRequestsTable", "errorsTable",
    "errorCount", "requestDetailsBackdrop", "requestDetailsModal",
    "requestDetailsSubtitle", "requestDetailsContent"
  ]

  connect() {
    this.charts = {}
    this.allRequests = []
    this.currentFilter = 'all'
    this.boundCloseDetailsOnEscape = this.closeRequestDetailsOnEscape.bind(this)
    document.addEventListener('keydown', this.boundCloseDetailsOnEscape)

    // Wait for ECharts to be available
    if (typeof echarts === 'undefined') {
      console.error('ECharts not loaded')
      return
    }

    this.initCharts()
    this.refreshData()

    // Auto-refresh every 30 seconds
    this.refreshInterval = setInterval(() => this.refreshData(), 30000)

    // Setup filter badges
    this.element.querySelectorAll('.filter-badge').forEach(badge => {
      badge.addEventListener('click', (e) => this.handleFilterClick(e))
    })
  }

  disconnect() {
    if (this.refreshInterval) {
      clearInterval(this.refreshInterval)
    }
    Object.values(this.charts).forEach(chart => {
      if (chart && typeof chart.dispose === 'function') {
        chart.dispose()
      }
    })
    document.removeEventListener('keydown', this.boundCloseDetailsOnEscape)
    document.body.classList.remove('nfr-modal-open')
  }

  // ============ Theme Colors ============

  getThemeColors() {
    const isDark = document.documentElement.dataset.nfrTheme === 'dark' ||
      document.documentElement.classList.contains('nfr-theme-dark')

    return {
      primary: '#2f80ed',
      success: '#2f6f59',
      warning: '#a15c00',
      danger: '#c0343d',
      info: '#057f95',
      secondary: '#5b6578',
      textPrimary: isDark ? '#e3e6ed' : '#141824',
      textSecondary: isDark ? '#9fa6bc' : '#5b6578',
      border: isDark ? '#373e53' : 'rgba(15, 23, 42, 0.1)',
      background: isDark ? '#141824' : '#ffffff',
      cardBg: isDark ? '#1e2435' : '#ffffff'
    }
  }

  // ============ Chart Initialization ============

  initCharts() {
    const colors = this.getThemeColors()

    if (this.hasRequestsChartTarget) {
      this.charts.requests = echarts.init(this.requestsChartTarget)
      this.charts.requests.setOption(this.getLineChartOption([], [], colors))
    }

    if (this.hasEndpointChartTarget) {
      this.charts.endpoint = echarts.init(this.endpointChartTarget)
      this.charts.endpoint.setOption(this.getPieChartOption([], colors))
    }

    if (this.hasQueryTypeChartTarget) {
      this.charts.queryType = echarts.init(this.queryTypeChartTarget)
      this.charts.queryType.setOption(this.getPieChartOption([], colors))
    }

    if (this.hasHourlyChartTarget) {
      this.charts.hourly = echarts.init(this.hourlyChartTarget)
      this.charts.hourly.setOption(this.getBarChartOption([], [], colors))
    }

    window.addEventListener('resize', () => this.resizeCharts())
  }

  resizeCharts() {
    Object.values(this.charts).forEach(chart => {
      if (chart && typeof chart.resize === 'function') {
        chart.resize()
      }
    })
  }

  // ============ Chart Options ============

  getLineChartOption(labels, values, colors) {
    const hasData = labels && labels.length > 0

    if (!hasData) {
      return {
        title: {
          text: 'No data available',
          left: 'center',
          top: 'center',
          textStyle: { color: colors.textSecondary, fontSize: 14, fontWeight: 'normal' }
        },
        xAxis: { show: false },
        yAxis: { show: false },
        series: []
      }
    }

    return {
      tooltip: {
        trigger: 'axis',
        backgroundColor: colors.cardBg,
        borderColor: colors.border,
        textStyle: { color: colors.textPrimary }
      },
      grid: { left: 50, right: 20, top: 20, bottom: 30 },
      xAxis: {
        type: 'category',
        data: labels,
        axisLine: { lineStyle: { color: colors.border } },
        axisLabel: { color: colors.textSecondary, fontSize: 10, rotate: 45 }
      },
      yAxis: {
        type: 'value',
        axisLine: { show: false },
        axisLabel: { color: colors.textSecondary },
        splitLine: { lineStyle: { color: colors.border, type: 'dashed' } }
      },
      series: [{
        name: 'Requests',
        type: 'line',
        smooth: true,
        symbol: 'circle',
        symbolSize: 6,
        areaStyle: {
          color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
            { offset: 0, color: colors.primary + '40' },
            { offset: 1, color: colors.primary + '05' }
          ])
        },
        lineStyle: { color: colors.primary, width: 2 },
        itemStyle: { color: colors.primary },
        data: values
      }]
    }
  }

  getBarChartOption(labels, values, colors) {
    const hasData = labels && labels.length > 0

    if (!hasData) {
      return {
        title: {
          text: 'No data available',
          left: 'center',
          top: 'center',
          textStyle: { color: colors.textSecondary, fontSize: 14, fontWeight: 'normal' }
        },
        xAxis: { show: false },
        yAxis: { show: false },
        series: []
      }
    }

    return {
      tooltip: {
        trigger: 'axis',
        backgroundColor: colors.cardBg,
        borderColor: colors.border,
        textStyle: { color: colors.textPrimary }
      },
      grid: { left: 40, right: 10, top: 10, bottom: 50 },
      xAxis: {
        type: 'category',
        data: labels,
        axisLine: { lineStyle: { color: colors.border } },
        axisLabel: { color: colors.textSecondary, fontSize: 9, rotate: 45 }
      },
      yAxis: {
        type: 'value',
        axisLine: { show: false },
        axisLabel: { color: colors.textSecondary },
        splitLine: { lineStyle: { color: colors.border, type: 'dashed' } }
      },
      series: [{
        type: 'bar',
        data: values.map(v => ({
          value: v,
          itemStyle: { color: colors.primary, borderRadius: [4, 4, 0, 0] }
        })),
        barWidth: '60%'
      }]
    }
  }

  getPieChartOption(data, colors) {
    const hasData = data && data.length > 0 && data.some(d => d.value > 0)

    if (!hasData) {
      return {
        title: {
          text: 'No data available',
          left: 'center',
          top: 'center',
          textStyle: { color: colors.textSecondary, fontSize: 14, fontWeight: 'normal' }
        },
        series: []
      }
    }

    return {
      tooltip: {
        trigger: 'item',
        backgroundColor: colors.cardBg,
        borderColor: colors.border,
        textStyle: { color: colors.textPrimary },
        formatter: '{b}: {c} ({d}%)'
      },
      legend: {
        orient: 'vertical',
        right: 10,
        top: 'center',
        textStyle: { color: colors.textSecondary, fontSize: 11 }
      },
      series: [{
        type: 'pie',
        radius: ['45%', '70%'],
        center: ['35%', '50%'],
        avoidLabelOverlap: false,
        itemStyle: { borderRadius: 4, borderColor: colors.background, borderWidth: 2 },
        label: { show: false },
        emphasis: { label: { show: true, fontSize: 14, fontWeight: 'bold' } },
        data: data
      }]
    }
  }

  // ============ Data Fetching ============

  async refreshData() {
    try {
      const response = await fetch('/admin/api_dashboard/usage_data', {
        headers: { 'Accept': 'application/json' }
      })
      const data = await response.json()

      this.updateOverview(data.overview)
      this.updateCharts(data)
      this.updateTables(data)

    } catch (error) {
      console.error('Error refreshing data:', error)
      if (typeof toastr !== 'undefined') {
        toastr.error('Failed to refresh API dashboard data')
      }
    }
  }

  refresh() {
    this.refreshData()
  }

  updateOverview(overview) {
    if (this.hasTotalRequestsTarget) {
      this.totalRequestsTarget.textContent = (overview.total_requests || 0).toLocaleString()
    }
    if (this.hasRequestsTodayTarget) {
      this.requestsTodayTarget.textContent = (overview.requests_today || 0).toLocaleString()
    }
    if (this.hasUniqueUsersTarget) {
      this.uniqueUsersTarget.textContent = (overview.unique_users_total || 0).toLocaleString()
    }
    if (this.hasSuccessRateTarget) {
      this.successRateTarget.textContent = (overview.success_rate || 0) + '%'
    }
    if (this.hasGraphqlRequestsTarget) {
      this.graphqlRequestsTarget.textContent = (overview.graphql_requests || 0).toLocaleString()
    }
    if (this.hasRestRequestsTarget) {
      this.restRequestsTarget.textContent = (overview.rest_requests || 0).toLocaleString()
    }
    if (this.hasAvgResponseTimeTarget) {
      this.avgResponseTimeTarget.textContent = (overview.avg_response_time || 0) + 'ms'
    }
    if (this.hasTotalItemsServedTarget) {
      this.totalItemsServedTarget.textContent = (overview.total_items_served || 0).toLocaleString()
    }
  }

  updateCharts(data) {
    const colors = this.getThemeColors()

    // Requests over time chart
    if (this.charts.requests && data.requests_by_day) {
      const labels = Object.keys(data.requests_by_day).map(d => {
        const date = new Date(d)
        return `${date.getMonth() + 1}/${date.getDate()}`
      })
      const values = Object.values(data.requests_by_day)
      this.charts.requests.setOption(this.getLineChartOption(labels, values, colors))
    }

    // Endpoint breakdown
    if (this.charts.endpoint && data.endpoint_breakdown) {
      const pieData = [
        { name: 'GraphQL', value: data.endpoint_breakdown.values[0] || 0, itemStyle: { color: colors.primary } },
        { name: 'REST RSS', value: data.endpoint_breakdown.values[1] || 0, itemStyle: { color: colors.success } }
      ]
      this.charts.endpoint.setOption(this.getPieChartOption(pieData, colors))
    }

    // Query type breakdown
    if (this.charts.queryType && data.query_type_breakdown) {
      const queryColors = [colors.info, colors.warning, colors.danger, colors.secondary]
      const pieData = data.query_type_breakdown.labels.map((label, i) => ({
        name: label,
        value: data.query_type_breakdown.values[i] || 0,
        itemStyle: { color: queryColors[i % queryColors.length] }
      }))
      this.charts.queryType.setOption(this.getPieChartOption(pieData, colors))
    }

    // Hourly chart
    if (this.charts.hourly && data.requests_by_hour) {
      const labels = Object.keys(data.requests_by_hour)
      const values = Object.values(data.requests_by_hour)
      this.charts.hourly.setOption(this.getBarChartOption(labels, values, colors))
    }
  }

  updateTables(data) {
    // Performance table
    if (this.hasPerformanceTableTarget && data.performance_stats) {
      const stats = data.performance_stats
      this.performanceTableTarget.innerHTML = `
        <tr><td class="nfr-muted-text">Avg GraphQL</td><td class="nfr-table-cell-end nfr-admin-strong">${stats.avg_duration_graphql}ms</td></tr>
        <tr><td class="nfr-muted-text">Avg REST</td><td class="nfr-table-cell-end nfr-admin-strong">${stats.avg_duration_rest}ms</td></tr>
        <tr><td class="nfr-muted-text">Max GraphQL</td><td class="nfr-table-cell-end nfr-admin-strong">${stats.max_duration_graphql}ms</td></tr>
        <tr><td class="nfr-muted-text">Max REST</td><td class="nfr-table-cell-end nfr-admin-strong">${stats.max_duration_rest}ms</td></tr>
        <tr><td class="nfr-muted-text">P95</td><td class="nfr-table-cell-end nfr-admin-strong">${stats.percentile_95}ms</td></tr>
        <tr><td class="nfr-muted-text">P99</td><td class="nfr-table-cell-end nfr-admin-strong">${stats.percentile_99}ms</td></tr>
        <tr><td class="nfr-muted-text">Avg Items/Req</td><td class="nfr-table-cell-end nfr-admin-strong">${stats.avg_items_per_request}</td></tr>
        <tr><td class="nfr-muted-text">Avg Feeds/Req</td><td class="nfr-table-cell-end nfr-admin-strong">${stats.avg_feeds_per_request}</td></tr>
      `
    }

    // Top users table
    if (this.hasTopUsersTableTarget && data.top_users) {
      this.topUsersTableTarget.innerHTML = data.top_users.length > 0
        ? data.top_users.map(user => `
          <tr>
            <td data-label="User">
              <span class="nfr-admin-strong">${this.escapeHtml(user.name)}</span>
              <br><small class="nfr-muted-text">${this.escapeHtml(user.email)}</small>
            </td>
            <td data-label="Requests" class="nfr-table-cell-end">${user.request_count.toLocaleString()}</td>
            <td data-label="Last Active"><small class="nfr-muted-text">${this.formatDate(user.last_request)}</small></td>
          </tr>
        `).join('')
        : '<tr><td colspan="3" class="nfr-admin-table-empty">No API users yet</td></tr>'
    }

    // Recent requests table
    if (this.hasRecentRequestsTableTarget && data.recent_requests) {
      this.allRequests = data.recent_requests
      this.renderRecentRequestsTable()
    }

    // Errors table
    if (this.hasErrorsTableTarget && data.error_stats) {
      if (this.hasErrorCountTarget) {
        this.errorCountTarget.textContent = data.error_stats.total_errors
      }

      this.errorsTableTarget.innerHTML = data.error_stats.recent_errors.length > 0
        ? data.error_stats.recent_errors.map(error => `
          <tr>
            <td data-label="Endpoint"><span class="nfr-badge ${error.endpoint === 'graphql' ? 'nfr-badge-primary' : 'nfr-badge-success'}">${error.endpoint}</span></td>
            <td data-label="Query Type">${error.query_type || '-'}</td>
            <td data-label="Status"><span class="nfr-badge nfr-badge-danger">${error.response_status}</span></td>
            <td data-label="Error"><small class="nfr-admin-danger-text">${this.escapeHtml(error.error_message || 'Unknown error')}</small></td>
            <td data-label="User"><small class="nfr-muted-text">${this.escapeHtml(error.user_name)}</small></td>
            <td data-label="Time"><small class="nfr-muted-text">${this.formatDate(error.created_at)}</small></td>
          </tr>
        `).join('')
        : '<tr><td colspan="6" class="nfr-admin-table-empty">No errors recorded</td></tr>'
    }
  }

  renderRecentRequestsTable() {
    const filtered = this.allRequests.filter(req => {
      if (this.currentFilter === 'all') return true
      return req.endpoint === this.currentFilter
    })

    this.recentRequestsTableTarget.innerHTML = filtered.length > 0
      ? filtered.slice(0, 30).map(req => `
        <tr class="nfr-admin-clickable-row" role="button" tabindex="0" data-action="click->api-dashboard#showRequestDetails keydown.enter->api-dashboard#showRequestDetails keydown.space->api-dashboard#showRequestDetails" data-request-id="${req.id}">
          <td data-label="Endpoint">
            <span class="nfr-badge ${req.endpoint === 'graphql' ? 'nfr-badge-primary' : 'nfr-badge-success'}">${req.endpoint}</span>
            ${req.query_type ? `<br><small class="nfr-muted-text">${req.query_type}</small>` : ''}
          </td>
          <td data-label="User"><small>${this.escapeHtml(req.user_name)}</small></td>
          <td data-label="Status"><span class="nfr-badge ${req.response_status >= 200 && req.response_status < 300 ? 'nfr-badge-success' : 'nfr-badge-danger'}">${req.response_status}</span></td>
          <td data-label="Time" class="nfr-table-cell-end"><small>${req.duration_ms}ms</small></td>
          <td data-label="When"><small class="nfr-muted-text">${this.formatDate(req.created_at)}</small></td>
        </tr>
      `).join('')
      : '<tr><td colspan="5" class="nfr-admin-table-empty">No requests yet</td></tr>'
  }

  handleFilterClick(event) {
    const badge = event.currentTarget
    this.element.querySelectorAll('.filter-badge').forEach(b => b.classList.remove('active'))
    badge.classList.add('active')
    this.currentFilter = badge.dataset.filter
    this.renderRecentRequestsTable()
  }

  showRequestDetails(event) {
    if (event.type === 'keydown' && !['Enter', ' '].includes(event.key)) return
    if (!this.hasRequestDetailsModalTarget) return

    if (event.type === 'keydown') {
      event.preventDefault()
    }

    const row = event.currentTarget
    const request = this.allRequests.find(req => String(req.id) === row.dataset.requestId)
    if (!request) return

    if (this.hasRequestDetailsSubtitleTarget) {
      this.requestDetailsSubtitleTarget.textContent = `${request.endpoint.toUpperCase()} ${request.method} - ${this.formatAbsoluteDate(request.created_at)}`
    }

    if (this.hasRequestDetailsContentTarget) {
      this.requestDetailsContentTarget.innerHTML = this.buildRequestDetailsHtml(request)
    }

    this.openRequestDetails()
  }

  openRequestDetails() {
    if (this.hasRequestDetailsBackdropTarget) {
      this.requestDetailsBackdropTarget.hidden = false
    }
    this.requestDetailsModalTarget.hidden = false
    this.requestDetailsModalTarget.setAttribute('aria-hidden', 'false')
    document.body.classList.add('nfr-modal-open')

    requestAnimationFrame(() => {
      if (this.hasRequestDetailsBackdropTarget) {
        this.requestDetailsBackdropTarget.classList.add('is-open')
      }
      this.requestDetailsModalTarget.classList.add('is-open')
    })
  }

  hideRequestDetails(event) {
    event?.preventDefault()
    if (!this.hasRequestDetailsModalTarget) return

    if (this.hasRequestDetailsBackdropTarget) {
      this.requestDetailsBackdropTarget.classList.remove('is-open')
    }
    this.requestDetailsModalTarget.classList.remove('is-open')
    this.requestDetailsModalTarget.setAttribute('aria-hidden', 'true')
    document.body.classList.remove('nfr-modal-open')

    window.setTimeout(() => {
      if (this.hasRequestDetailsBackdropTarget) {
        this.requestDetailsBackdropTarget.hidden = true
      }
      this.requestDetailsModalTarget.hidden = true
    }, 180)
  }

  closeRequestDetailsOnEscape(event) {
    if (event.key === 'Escape' && this.hasRequestDetailsModalTarget && !this.requestDetailsModalTarget.hidden) {
      this.hideRequestDetails(event)
    }
  }

  buildRequestDetailsHtml(request) {
    const metadata = [
      ['User', request.user_name || 'Anonymous'],
      ['Query Type', request.query_type || '-'],
      ['Operation', request.operation_name || '-'],
      ['Status', request.response_status],
      ['Duration', request.duration_ms != null ? `${request.duration_ms}ms` : '-'],
      ['Feeds', request.feed_count != null ? request.feed_count : '-'],
      ['Items', request.item_count != null ? request.item_count : '-'],
      ['IP Address', request.ip_address || '-']
    ]

    const sections = [
      `
        <section class="nfr-admin-detail-section">
          <h3 class="nfr-admin-detail-title">Summary</h3>
          <div class="nfr-admin-detail-grid">
            ${metadata.map(([label, value]) => `
              <div class="nfr-admin-detail-card">
                <div class="nfr-admin-detail-label">${this.escapeHtml(label)}</div>
                <div class="nfr-admin-strong">${this.escapeHtml(String(value))}</div>
              </div>
            `).join('')}
          </div>
        </section>
      `
    ]

    if (request.query_text) {
      sections.push(this.buildCodeSection('Query', request.query_text))
    }

    if (request.variables && Object.keys(request.variables).length > 0) {
      sections.push(this.buildCodeSection('Variables', this.prettyJson(request.variables)))
    }

    if (request.request_details && Object.keys(request.request_details).length > 0) {
      sections.push(this.buildCodeSection('Request Details', this.prettyJson(request.request_details)))
    }

    if (request.error_message) {
      sections.push(this.buildCodeSection('Error', request.error_message))
    }

    if (sections.length === 1) {
      sections.push('<p class="nfr-muted-text">Detailed request payloads were not captured for this log entry.</p>')
    }

    return sections.join('')
  }

  buildCodeSection(title, content) {
    return `
      <section class="nfr-admin-detail-section">
        <h3 class="nfr-admin-detail-title">${this.escapeHtml(title)}</h3>
        <pre class="nfr-admin-code-block">${this.escapeHtml(content)}</pre>
      </section>
    `
  }

  prettyJson(value) {
    return JSON.stringify(value, null, 2)
  }

  escapeHtml(text) {
    if (!text) return ''
    const div = document.createElement('div')
    div.textContent = text
    return div.innerHTML
  }

  formatDate(isoString) {
    if (!isoString) return '-'
    const date = new Date(isoString)
    const now = new Date()
    const diffMs = now - date
    const diffMins = Math.floor(diffMs / 60000)
    const diffHours = Math.floor(diffMs / 3600000)
    const diffDays = Math.floor(diffMs / 86400000)

    if (diffMins < 1) return 'Just now'
    if (diffMins < 60) return `${diffMins}m ago`
    if (diffHours < 24) return `${diffHours}h ago`
    if (diffDays < 7) return `${diffDays}d ago`
    return date.toLocaleDateString()
  }

  formatAbsoluteDate(isoString) {
    if (!isoString) return '-'
    return new Date(isoString).toLocaleString()
  }
}
